import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Autocomplete,
  IconButton,
  Chip,
  Alert,
  Tabs,
  Tab,
  Card,
  CardContent,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Fab,
  Tooltip,
  Badge,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Collapse,
  TablePagination,
  Link,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Save as SaveIcon,
  Send as SendIcon,
  Article as DraftIcon,
  History as HistoryIcon,
  LocalShipping as ShippingIcon,
  CheckCircle as CompletedIcon,
  Cancel as CancelIcon,
  Refresh as RefreshIcon,
  FilterList as FilterIcon,
  DateRange as DateRangeIcon,
  Print as PrintIcon,
  Visibility as ViewIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { RootState } from '../store';
import {
  useGetTransfersQuery,
  useGetUserDraftsQuery,
  useCreateTransferMutation,
  useUpdateTransferMutation,
  useUpdateTransferStatusMutation,
  useCancelTransferMutation,
  Transfer,
  TransferItem,
  TransferStatus,
  CreateTransferRequest,
} from '../services/transfers.service';
import { useGetLocationsQuery } from '../services/locations.service';
import { useGetProductsQuery, useQuickSearchProductsQuery } from '../services/products.service';
import { formatCurrency } from '../utils/currency';
import { DatePicker } from '@mui/x-date-pickers';
import { toast } from 'react-toastify';
import TransferHistoryTab from '../components/TransferHistoryTab';
import TransferDetailDialog from '../components/TransferDetailDialog';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`transfer-tabpanel-${index}`}
      aria-labelledby={`transfer-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 0 }}>{children}</Box>}
    </div>
  );
}

const TransfersPage: React.FC = () => {
  const { user } = useSelector((state: RootState) => state.auth);
  const [tabValue, setTabValue] = useState(0);
  const [page, setPage] = useState(1);
  const [historyPage, setHistoryPage] = useState(1);
  const [showFilters, setShowFilters] = useState(false);
  const [statusFilter, setStatusFilter] = useState<TransferStatus | ''>('');
  const [fromLocationFilter, setFromLocationFilter] = useState<string>('');
  const [toLocationFilter, setToLocationFilter] = useState<string>('');
  const [dateFromFilter, setDateFromFilter] = useState<Date | null>(null);
  const [dateToFilter, setDateToFilter] = useState<Date | null>(null);
  const [detailDialog, setDetailDialog] = useState(false);
  const [transferDetail, setTransferDetail] = useState<Transfer | null>(null);
  const [createDialog, setCreateDialog] = useState(false);
  const [editDialog, setEditDialog] = useState(false);
  const [selectedTransfer, setSelectedTransfer] = useState<Transfer | null>(null);
  
  // Transfer form state
  const [fromLocationId, setFromLocationId] = useState<string>('');
  const [toLocationId, setToLocationId] = useState<string>('');
  const [transferItems, setTransferItems] = useState<TransferItem[]>([]);
  const [notes, setNotes] = useState('');
  const [productSearch, setProductSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedProduct, setSelectedProduct] = useState<any>(null);
  const [quantity, setQuantity] = useState(1);

  // Ref for product search input to maintain focus for barcode scanning
  const productSearchInputRef = useRef<HTMLInputElement>(null);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(productSearch);
    }, 300);

    return () => clearTimeout(timer);
  }, [productSearch]);

  // API calls
  const { data: locationsResponse } = useGetLocationsQuery({ active: true });
  const { data: productsResponse } = useQuickSearchProductsQuery(
    { q: debouncedSearch, limit: 50 },
    { skip: debouncedSearch.length < 2 }
  );
  const { data: transfersResponse, refetch: refetchTransfers } = useGetTransfersQuery({
    page,
    includeDrafts: false,
  });
  
  // History query with filters
  const { data: historyResponse, refetch: refetchHistory } = useGetTransfersQuery({
    page: historyPage,
    status: statusFilter || undefined,
    fromLocationId: fromLocationFilter || undefined,
    toLocationId: toLocationFilter || undefined,
    includeDrafts: true,
  });
  const { data: draftsResponse, refetch: refetchDrafts } = useGetUserDraftsQuery();
  
  // Mutations
  const [createTransfer, { isLoading: isCreating }] = useCreateTransferMutation();
  const [updateTransfer, { isLoading: isUpdating }] = useUpdateTransferMutation();
  const [updateTransferStatus] = useUpdateTransferStatusMutation();
  const [cancelTransfer] = useCancelTransferMutation();

  const locations = locationsResponse?.data || [];
  const products = productsResponse?.products || [];
  const transfers = transfersResponse?.data || [];
  const drafts = draftsResponse?.data || [];
  const historyTransfers = historyResponse?.data || [];
  const historyPagination = historyResponse?.pagination;

  useEffect(() => {
    if (user?.locationId) {
      setFromLocationId(user.locationId);
    }
  }, [user]);

  // Auto-add product when one is selected (useful for barcode scanning)
  useEffect(() => {
    if (selectedProduct && quantity > 0) {
      // Small delay to ensure the product is fully selected
      const timer = setTimeout(() => {
        // Inline the add product logic to avoid dependency issues
        const existingIndex = transferItems.findIndex(item => item.productId === selectedProduct.id);
        if (existingIndex >= 0) {
          const newItems = [...transferItems];
          newItems[existingIndex].quantity += quantity;
          setTransferItems(newItems);
        } else {
          setTransferItems(prev => [
            ...prev,
            {
              productId: selectedProduct.id,
              quantity,
              product: selectedProduct,
            },
          ]);
        }

        // Clear and refocus for next scan
        setSelectedProduct(null);
        setProductSearch('');
        setQuantity(1);

        setTimeout(() => {
          productSearchInputRef.current?.focus();
        }, 100);
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [selectedProduct, quantity, transferItems]);

  // Auto-focus the search input when dialog opens
  useEffect(() => {
    if (createDialog || editDialog) {
      setTimeout(() => {
        productSearchInputRef.current?.focus();
      }, 200);
    }
  }, [createDialog, editDialog]);

  const resetForm = () => {
    setFromLocationId(user?.locationId || '');
    setToLocationId('');
    setTransferItems([]);
    setNotes('');
    setSelectedProduct(null);
    setProductSearch('');
    setQuantity(1);
  };

  const handleAddProduct = () => {
    if (!selectedProduct || quantity <= 0) return;

    const existingIndex = transferItems.findIndex(item => item.productId === selectedProduct.id);
    if (existingIndex >= 0) {
      const newItems = [...transferItems];
      newItems[existingIndex].quantity += quantity;
      setTransferItems(newItems);
    } else {
      setTransferItems([
        ...transferItems,
        {
          productId: selectedProduct.id,
          quantity,
          product: selectedProduct,
        },
      ]);
    }

    setSelectedProduct(null);
    setProductSearch('');
    setQuantity(1);

    // Focus back on the product search input for continuous barcode scanning
    setTimeout(() => {
      productSearchInputRef.current?.focus();
    }, 100);
  };

  const handleRemoveProduct = (index: number) => {
    setTransferItems(transferItems.filter((_, i) => i !== index));
  };

  const handleUpdateQuantity = (index: number, newQuantity: number) => {
    if (newQuantity <= 0) {
      handleRemoveProduct(index);
      return;
    }
    const newItems = [...transferItems];
    newItems[index].quantity = newQuantity;
    setTransferItems(newItems);
  };

  const handleSave = async (isDraft: boolean) => {
    if (!fromLocationId || !toLocationId || transferItems.length === 0) {
      toast.error('Completa todos los campos requeridos');
      return;
    }

    if (fromLocationId === toLocationId) {
      toast.error('Las ubicaciones de origen y destino deben ser diferentes');
      return;
    }

    const transferData: CreateTransferRequest = {
      fromLocationId,
      toLocationId,
      items: transferItems.map(item => ({
        productId: item.productId,
        quantity: item.quantity,
      })),
      notes: notes.trim() || undefined,
      isDraft,
    };

    try {
      if (selectedTransfer) {
        await updateTransfer({
          id: selectedTransfer.id,
          data: transferData,
        }).unwrap();
        toast.success(isDraft ? 'Borrador actualizado' : 'Transferencia completada - inventario actualizado');
        setEditDialog(false);
      } else {
        await createTransfer(transferData).unwrap();
        toast.success(isDraft ? 'Borrador guardado' : 'Transferencia completada - inventario actualizado');
        setCreateDialog(false);
      }
      resetForm();
      refetchTransfers();
      refetchDrafts();
    } catch (error) {
      console.error('Error saving transfer:', error);
    }
  };

  const handleStatusChange = async (transferId: string, status: TransferStatus) => {
    try {
      await updateTransferStatus({ id: transferId, status }).unwrap();
      toast.success(`Estado actualizado a ${status}`);
      refetchTransfers();
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const handleCancel = async (transferId: string) => {
    const reason = prompt('Razón de cancelación:');
    if (!reason) return;

    try {
      await cancelTransfer({ id: transferId, reason }).unwrap();
      toast.success('Transferencia cancelada');
      refetchTransfers();
    } catch (error) {
      console.error('Error cancelling transfer:', error);
    }
  };

  const handleEditDraft = (transfer: Transfer) => {
    setSelectedTransfer(transfer);
    setFromLocationId(transfer.fromLocationId);
    setToLocationId(transfer.toLocationId);
    setTransferItems(transfer.items || []);
    setNotes(transfer.notes || '');
    setEditDialog(true);
  };

  const handleViewDetail = async (transfer: Transfer) => {
    setTransferDetail(transfer);
    setDetailDialog(true);
  };

  const clearFilters = () => {
    setStatusFilter('');
    setFromLocationFilter('');
    setToLocationFilter('');
    setDateFromFilter(null);
    setDateToFilter(null);
    setHistoryPage(1);
  };

  const getTotalItems = (items: TransferItem[]) => {
    return items.reduce((total, item) => total + item.quantity, 0);
  };

  const getTotalValue = (items: TransferItem[]) => {
    return items.reduce((total, item) => {
      const price = item.product?.price || 0;
      return total + (price * item.quantity);
    }, 0);
  };

  const getStatusColor = (status: TransferStatus) => {
    switch (status) {
      case TransferStatus.DRAFT:
        return 'default';
      case TransferStatus.PENDING:
        return 'warning';
      case TransferStatus.IN_TRANSIT:
        return 'info';
      case TransferStatus.COMPLETED:
        return 'success';
      case TransferStatus.CANCELLED:
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status: TransferStatus) => {
    switch (status) {
      case TransferStatus.DRAFT:
        return <DraftIcon />;
      case TransferStatus.PENDING:
        return <HistoryIcon />;
      case TransferStatus.IN_TRANSIT:
        return <ShippingIcon />;
      case TransferStatus.COMPLETED:
        return <CompletedIcon />;
      case TransferStatus.CANCELLED:
        return <CancelIcon />;
      default:
        return <HistoryIcon />;
    }
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom fontWeight="bold">
            Transferencias
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Gestión de transferencias entre ubicaciones
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => {
            resetForm();
            setCreateDialog(true);
          }}
          size="large"
        >
          Nueva Transferencia
        </Button>
      </Box>

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={tabValue} onChange={(_, newValue) => setTabValue(newValue)}>
          <Tab
            label="Transferencias"
            icon={<Badge badgeContent={transfers.length} color="primary"><HistoryIcon /></Badge>}
          />
          <Tab
            label={`Borradores (${drafts.length})`}
            icon={<DraftIcon />}
            disabled={drafts.length === 0}
          />
          <Tab
            label="Historial"
            icon={<SearchIcon />}
          />
        </Tabs>
      </Paper>

      {/* Tab Panels */}
      <TabPanel value={tabValue} index={0}>
        {/* Transfers List */}
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Número</TableCell>
                <TableCell>Estado</TableCell>
                <TableCell>Origen</TableCell>
                <TableCell>Destino</TableCell>
                <TableCell>Items</TableCell>
                <TableCell>Fecha</TableCell>
                <TableCell>Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {transfers.map((transfer) => (
                <TableRow key={transfer.id}>
                  <TableCell>
                    <Link
                      component="button"
                      variant="body2"
                      onClick={() => handleViewDetail(transfer)}
                      sx={{ fontWeight: 'bold', textDecoration: 'none' }}
                    >
                      {transfer.transferNumber || `DRAFT-${transfer.id.slice(0, 8)}`}
                    </Link>
                  </TableCell>
                  <TableCell>
                    <Chip
                      icon={getStatusIcon(transfer.status)}
                      label={transfer.status}
                      color={getStatusColor(transfer.status)}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>{transfer.fromLocation.name}</TableCell>
                  <TableCell>{transfer.toLocation.name}</TableCell>
                  <TableCell>{transfer.items.length} productos</TableCell>
                  <TableCell>
                    {new Date(transfer.createdAt).toLocaleDateString()}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      {transfer.status === TransferStatus.PENDING && (
                        <>
                          <Tooltip title="Marcar en tránsito">
                            <IconButton
                              size="small"
                              onClick={() => handleStatusChange(transfer.id, TransferStatus.IN_TRANSIT)}
                            >
                              <ShippingIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Completar">
                            <IconButton
                              size="small"
                              color="success"
                              onClick={() => handleStatusChange(transfer.id, TransferStatus.COMPLETED)}
                            >
                              <CompletedIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Cancelar">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleCancel(transfer.id)}
                            >
                              <CancelIcon />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                      {transfer.status === TransferStatus.IN_TRANSIT && (
                        <Tooltip title="Completar">
                          <IconButton
                            size="small"
                            color="success"
                            onClick={() => handleStatusChange(transfer.id, TransferStatus.COMPLETED)}
                          >
                            <CompletedIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        {/* Drafts List */}
        <Grid container spacing={2}>
          {drafts.map((draft) => (
            <Grid key={draft.id} size={{ xs: 12, sm: 6, md: 4 }}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Typography variant="h6" component="div">
                      Borrador
                    </Typography>
                    <Chip label="DRAFT" color="default" size="small" />
                  </Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    De: {draft.fromLocation.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Para: {draft.toLocation.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Items: {draft.items.length} productos
                  </Typography>
                  <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                    Actualizado: {new Date(draft.updatedAt).toLocaleDateString()}
                  </Typography>
                  <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<EditIcon />}
                      onClick={() => handleEditDraft(draft)}
                    >
                      Editar
                    </Button>
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<SendIcon />}
                      onClick={() => {
                        setSelectedTransfer(draft);
                        setFromLocationId(draft.fromLocationId);
                        setToLocationId(draft.toLocationId);
                        setTransferItems(draft.items || []);
                        setNotes(draft.notes || '');
                        handleSave(false);
                      }}
                    >
                      Enviar
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </TabPanel>

      <TabPanel value={tabValue} index={2}>
        {/* History Tab */}
        <TransferHistoryTab
          historyTransfers={historyTransfers}
          historyPagination={historyPagination}
          historyPage={historyPage}
          setHistoryPage={setHistoryPage}
          showFilters={showFilters}
          setShowFilters={setShowFilters}
          statusFilter={statusFilter}
          setStatusFilter={setStatusFilter}
          fromLocationFilter={fromLocationFilter}
          setFromLocationFilter={setFromLocationFilter}
          toLocationFilter={toLocationFilter}
          setToLocationFilter={setToLocationFilter}
          dateFromFilter={dateFromFilter}
          setDateFromFilter={setDateFromFilter}
          dateToFilter={dateToFilter}
          setDateToFilter={setDateToFilter}
          locations={locations}
          refetchHistory={refetchHistory}
          clearFilters={clearFilters}
          handleViewDetail={handleViewDetail}
          getStatusColor={getStatusColor}
          getStatusIcon={getStatusIcon}
          getTotalItems={getTotalItems}
          getTotalValue={getTotalValue}
        />
      </TabPanel>

      {/* Create/Edit Dialog */}
      <Dialog
        open={createDialog || editDialog}
        onClose={() => {
          setCreateDialog(false);
          setEditDialog(false);
          setSelectedTransfer(null);
          resetForm();
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editDialog ? 'Editar Transferencia' : 'Nueva Transferencia'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {/* Locations */}
            <Grid size={{ xs: 12, sm: 6 }}>
              <Autocomplete
                options={locations}
                getOptionLabel={(option) => option.name}
                value={locations.find(l => l.id === fromLocationId) || null}
                onChange={(_, location) => setFromLocationId(location?.id || '')}
                renderInput={(params) => (
                  <TextField {...params} label="Ubicación de Origen" required />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Autocomplete
                options={locations.filter(l => l.id !== fromLocationId)}
                getOptionLabel={(option) => option.name}
                value={locations.find(l => l.id === toLocationId) || null}
                onChange={(_, location) => setToLocationId(location?.id || '')}
                renderInput={(params) => (
                  <TextField {...params} label="Ubicación de Destino" required />
                )}
              />
            </Grid>

            {/* Product Search and Add */}
            <Grid size={{ xs: 12 }}>
              <Divider sx={{ my: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Productos a Transferir
                </Typography>
              </Divider>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Autocomplete
                options={products}
                getOptionLabel={(option) => {
                  const identifiers = [
                    option.barcode,
                    option.sku,
                    option.alternativeCode,
                    ...(option.alternativeBarcodes || [])
                  ].filter(Boolean);
                  const codes = identifiers.length > 0 ? identifiers.join(' | ') : 'Sin código';
                  return `${option.name} (${codes})`;
                }}
                value={selectedProduct}
                onChange={(_, product) => {
                  setSelectedProduct(product);
                }}
                inputValue={productSearch}
                onInputChange={(_, value, reason) => {
                  // Allow all input changes including from barcode scanner
                  if (reason !== 'reset') {
                    setProductSearch(value);
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Buscar Producto"
                    placeholder="Buscar por nombre, SKU, código de barras o código alternativo"
                    inputRef={productSearchInputRef}
                    autoFocus
                    onKeyDown={(e) => {
                      // Handle Enter key from barcode scanner
                      if (e.key === 'Enter' && productSearch) {
                        e.preventDefault();
                        // If there's exactly one matching product, select it automatically
                        const products = productsResponse || [];
                        if (products.length === 1) {
                          setSelectedProduct(products[0]);
                        } else if (products.length > 1) {
                          // Check for exact barcode match
                          const exactMatch = products.find(p =>
                            p.barcode === productSearch ||
                            p.sku === productSearch ||
                            p.alternativeBarcodes?.includes(productSearch)
                          );
                          if (exactMatch) {
                            setSelectedProduct(exactMatch);
                          }
                        }
                      }
                    }}
                  />
                )}
                renderOption={(props, option) => (
                  <Box component="li" {...props} sx={{ flexDirection: 'column', alignItems: 'flex-start' }}>
                    <Typography variant="body1">{option.name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      SKU: {option.sku} | Barcode: {option.barcode || 'N/A'}
                      {option.alternativeCode && ` | Alt: ${option.alternativeCode}`}
                    </Typography>
                  </Box>
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                type="number"
                label="Cantidad"
                value={quantity}
                onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                inputProps={{ min: 1 }}
                fullWidth
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 2 }}>
              <Button
                variant="contained"
                fullWidth
                sx={{ height: '56px' }}
                onClick={handleAddProduct}
                disabled={!selectedProduct}
              >
                <AddIcon />
              </Button>
            </Grid>

            {/* Transfer Items List */}
            {transferItems.length > 0 && (
              <Grid size={{ xs: 12 }}>
                <List>
                  {transferItems.map((item, index) => (
                    <ListItem key={`${item.productId}-${index}`} divider>
                      <ListItemText
                        primary={item.product?.name || item.productId}
                        secondary={`Cantidad: ${item.quantity}`}
                      />
                      <ListItemSecondaryAction>
                        <TextField
                          size="small"
                          type="number"
                          value={item.quantity}
                          onChange={(e) => handleUpdateQuantity(index, parseInt(e.target.value) || 0)}
                          inputProps={{ min: 1 }}
                          sx={{ width: 80, mr: 1 }}
                        />
                        <IconButton
                          edge="end"
                          color="error"
                          onClick={() => handleRemoveProduct(index)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              </Grid>
            )}

            {/* Notes */}
            <Grid size={{ xs: 12 }}>
              <TextField
                label="Notas (opcional)"
                multiline
                rows={3}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                fullWidth
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setCreateDialog(false);
              setEditDialog(false);
              setSelectedTransfer(null);
              resetForm();
            }}
          >
            Cancelar
          </Button>
          <Button
            variant="outlined"
            startIcon={<SaveIcon />}
            onClick={() => handleSave(true)}
            disabled={isCreating || isUpdating}
          >
            {isCreating || isUpdating ? 'Guardando...' : 'Guardar Borrador'}
          </Button>
          <Button
            variant="contained"
            startIcon={<SendIcon />}
            onClick={() => handleSave(false)}
            disabled={isCreating || isUpdating}
          >
            {isCreating || isUpdating ? 'Transfiriendo...' : 'Transferir'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Transfer Detail Dialog */}
      <TransferDetailDialog
        open={detailDialog}
        transfer={transferDetail}
        onClose={() => {
          setDetailDialog(false);
          setTransferDetail(null);
        }}
        getStatusColor={getStatusColor}
        getStatusIcon={getStatusIcon}
        getTotalItems={getTotalItems}
        getTotalValue={getTotalValue}
      />
    </Box>
  );
};

export default TransfersPage;